import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Field, inputClass } from '../../components/ui/Field';
import { attendanceSummary } from '../../utils/attendance';
import { eventTitle, formatDate, functionLabel, functionOrder, fullName, memberTypeClass, memberTypeLabel, memberTypeOrder, voiceClass, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, MemberType, Voice } from '../../types';
export function AdminMemberDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    members,
    settings,
    updateMember,
    fees,
    setFeePaid,
    past,
    attendance,
    isExpected,
    pushToast
  } = useApp();
  const member = members.find(m => m.id === id);
  const [urejanje, setUrejanje] = useState(false);
  const [osnutek, setOsnutek] = useState(member);
  const zgodovina = useMemo(() => {
    if (!member) return [];
    return past.filter(e => e.steje && isExpected(e, member.id)).slice(0, 6).map(e => ({
      e,
      mark: attendance[e.id]?.[member.id] ?? 'prisoten'
    }));
  }, [past, attendance, isExpected, member]);
  if (!member || !osnutek) {
    return <Card className="p-10 text-center" data-id="element-1120" data-mp="2jqhqu5kjd0kv">
        <p className="text-sm font-medium text-ink" data-id="element-1121" data-mp="2jp6psdih66xg">Član ne obstaja.</p>
        <Link to="/admin/clani" className="mt-2 inline-block text-[13px] font-medium text-green-600" data-id="element-1122" data-mp="2ery8ef55i6x9">
          Nazaj na seznam
        </Link>
      </Card>;
  }
  const s = attendanceSummary(member, settings);
  const clanarine = fees.filter(f => f.memberId === member.id).sort((a, b) => a.leto - b.leto);
  function toggleFunkcija(f: ChoirFunction) {
    if (!osnutek) return;
    setOsnutek({
      ...osnutek,
      functions: osnutek.functions.includes(f) ? osnutek.functions.filter(x => x !== f) : [...osnutek.functions, f]
    });
  }
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-1123" data-mp="10rtodjmxyu5z">
      <button onClick={() => navigate('/admin/clani')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-1124" data-mp="10qik7ipl3kec">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1125" data-mp="quyq91ukyvs8" />
        Nazaj na člane
      </button>

      <Card className="p-5" data-id="element-1126" data-mp="cv61vd6j0rnf">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1127" data-mp="cvz5446a0z0t">
          <div className="flex items-center gap-4" data-id="element-1128" data-mp="hthchq4nb66p">
            <Avatar member={member} size="lg" data-id="element-1129" data-mp="2785gp4331htn" />
            <div data-id="element-1130" data-mp="278f4qm4khewp">
              <h1 className="text-xl font-semibold text-ink" data-id="element-1131" data-mp="275swek9uqvdf">{fullName(member)}</h1>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1132" data-mp="2c677kb6r2lfo">Član/ica od {formatDate(member.clanOd)}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-[13px] text-ink-muted" data-id="element-1133" data-mp="jwrdo67ru07">
                <span className="flex items-center gap-1.5" data-id="element-1134" data-mp="3rrhl1tzb5aqs">
                  <MailIcon className="h-3.5 w-3.5" data-id="element-1135" data-mp="a9y97y7z4g6o" />
                  {member.email}
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1136" data-mp="5ige3qsmt9tm">
                  <PhoneIcon className="h-3.5 w-3.5" data-id="element-1137" data-mp="ja4n3l7l3i6d" />
                  {member.telefon}
                </span>
              </div>
            </div>
          </div>
          {urejanje ? <div className="flex gap-2" data-id="element-1138" data-mp="ede6mwrvakkf">
              <Button variant="secondary" onClick={() => {
            setOsnutek(member);
            setUrejanje(false);
          }} data-id="element-1139" data-mp="t8kgjuu333j1">
                Prekliči
              </Button>
              <Button onClick={() => {
            updateMember(osnutek);
            setUrejanje(false);
            pushToast('Podatki člana so shranjeni.');
          }} data-id="element-1140" data-mp="2v1rtixm8591r">
                Shrani
              </Button>
            </div> : <Button variant="secondary" onClick={() => setUrejanje(true)} data-id="element-1141" data-mp="1m4jv4iuk1cj2">
              Uredi podatke
            </Button>}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]" data-id="element-1142" data-mp="1v23ypyfw3kwu">
        <Card className="p-5" data-id="element-1143" data-mp="ydehmac36wbx">
          <h2 className="text-base font-semibold text-ink" data-id="element-1144" data-mp="ye7duhdf63ft">Članstvo</h2>
          {urejanje ? <div className="mt-4 space-y-4" data-id="element-1145" data-mp="13dak2554yfpd">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-1146" data-mp="139v30wrwiwlb">
                <Field label="Tip članstva" htmlFor="tip" data-id="element-1147" data-mp="17d16xxrjkbom">
                  <select id="tip" value={osnutek.type} onChange={e => setOsnutek({
                ...osnutek,
                type: e.target.value as MemberType
              })} className={inputClass} data-id="element-1148" data-mp="1c4avsw59nazg">
                    {memberTypeOrder.map((t: MemberType) => <option key={t} value={t} data-id="element-1149" data-mp="c3o60vj4g3ox">
                        {memberTypeLabel[t]}
                      </option>)}
                  </select>
                </Field>
                <Field label="Glasovna skupina" htmlFor="glas" data-id="element-1150" data-mp="339idpioqci9">
                  <select id="glas" value={osnutek.voice} onChange={e => setOsnutek({
                ...osnutek,
                voice: e.target.value as Voice
              })} className={inputClass} data-id="element-1151" data-mp="3uacb4le68qul">
                    {voiceOrder.map(v => <option key={v} value={v} data-id="element-1152" data-mp="3fl6d30r2l6oa">
                        {voiceLabel[v]}
                      </option>)}
                  </select>
                </Field>
              </div>
              <div data-id="element-1153" data-mp="36law2fau2hgc">
                <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-1154" data-mp="36jq0cmcs8sgw">Funkcije v zboru</p>
                <div className="flex flex-wrap gap-2" data-id="element-1155" data-mp="31leqibmhmhyo">
                  {functionOrder.map(f => {
                const izbrana = osnutek.functions.includes(f);
                return <button key={f} type="button" onClick={() => toggleFunkcija(f)} aria-pressed={izbrana} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${izbrana ? 'border-green-300 bg-green-50 text-green-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-1156" data-mp="2wxfcsdn969dt">
                        {functionLabel[f]}
                      </button>;
              })}
                </div>
              </div>
              <Field label="Interna opomba" htmlFor="opomba" data-id="element-1157" data-mp="3tm3jidtiqa7x">
                <textarea id="opomba" rows={3} value={osnutek.opomba ?? ''} onChange={e => setOsnutek({
              ...osnutek,
              opomba: e.target.value
            })} className={inputClass} data-id="element-1158" data-mp="290jkhtv3l9m" />
              </Field>
            </div> : <div className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-1159" data-mp="1wofk7wy9cils">
              <div data-id="element-1160" data-mp="1rpm7w7upixn7">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1161" data-mp="1rr721wd40ekj">Tip članstva</p>
                <div className="mt-1.5" data-id="element-1162" data-mp="140tiku8lbo1k">
                  <Badge className={memberTypeClass[member.type]} data-id="element-1163" data-mp="z1gn5l3nl8nd">{memberTypeLabel[member.type]}</Badge>
                </div>
              </div>
              <div data-id="element-1164" data-mp="1d1re0o4yqn8j">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1165" data-mp="1d2azgettntrb">Glas</p>
                <div className="mt-1.5" data-id="element-1166" data-mp="191h9lfwtk4vs">
                  <Badge className={voiceClass[member.voice]} data-id="element-1167" data-mp="2oflqkv4kncji">{voiceLabel[member.voice]}</Badge>
                </div>
              </div>
              <div data-id="element-1168" data-mp="2ke072qxj800c">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1169" data-mp="2kgmd2m4ry7gg">Funkcije</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1170" data-mp="hq1lsi4wasj4">
                  {member.functions.map(f => <Badge key={f} data-id="element-1171" data-mp="rqc1qaibee1z">{functionLabel[f]}</Badge>)}
                </div>
              </div>
              {member.opomba && <p className="sm:col-span-3 rounded-lg bg-canvas px-3 py-2.5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1172" data-mp="wibyxv702suv">
                  {member.opomba}
                </p>}
            </div>}
        </Card>

        <Card className="p-5" data-id="element-1173" data-mp="1agjzse75qebb">
          <div className="flex items-start justify-between gap-3" data-id="element-1174" data-mp="1aga9epi7a553">
            <h2 className="text-base font-semibold text-ink" data-id="element-1175" data-mp="1ffmscz5yas2e">Prisotnost</h2>
            {member.type === 'aktivni' && <StatusBadge status={s.status} odstotek={s.odstotek} data-id="element-1176" data-mp="3c86htq92myvn" />}
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1177" data-mp="3ca9jj7jl6gg7">
            {s.prisoten} od {s.pricakovan} dogodkov · sezona 2026/27
          </p>
          <ul className="mt-3 divide-y divide-line" data-id="element-1178" data-mp="1ycev54fc5ehf">
            {zgodovina.map(({
            e,
            mark
          }) => <li key={e.id} className="flex items-center gap-3 py-2.5" data-id="element-1179" data-mp="1tkwwin0sbpzm">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${mark === 'prisoten' ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'}`} data-id="element-1180" data-mp="1pjl64fa370qd">
                  {mark === 'prisoten' ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1181" data-mp="2h8n3ih3na5gy" /> : <XIcon className="h-3.5 w-3.5" data-id="element-1182" data-mp="2d8mek2ix6bvn" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-1183" data-mp="28a2s810eyy2r">{eventTitle(e)}</span>
                <span className="shrink-0 text-[12px] text-ink-muted" data-id="element-1184" data-mp="23aykzfx1vaf2">{formatDate(e.datum)}</span>
              </li>)}
            {zgodovina.length === 0 && <li className="py-4 text-[13px] text-ink-muted" data-id="element-1185" data-mp="wstbhm8o6k55">Ni zabeleženih dogodkov.</li>}
          </ul>
        </Card>
      </div>

      <Card className="p-5" data-id="element-1186" data-mp="35yu3zojtp0my">
        <h2 className="text-base font-semibold text-ink" data-id="element-1187" data-mp="36056zm5g24d0">Članarine</h2>
        <ul className="mt-3 divide-y divide-line" data-id="element-1188" data-mp="310shetn6uj6q">
          {clanarine.map(f => <li key={f.leto} className="flex items-center gap-3 py-3 text-sm" data-id="element-1189" data-mp="3fowvtlf7k8jp">
              <span className="w-16 font-medium text-ink" data-id="element-1190" data-mp="3fqzq4hne8nu8">{f.leto}</span>
              <span className="flex-1 text-ink-muted" data-id="element-1191" data-mp="3ax5bvtqk512j">
                {f.znesek} €{f.datumPlacila ? ` · plačano ${formatDate(f.datumPlacila)}` : ''}
              </span>
              {f.placano ? <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1192" data-mp="3jqiexoic5j2c">
                  Poravnano
                </Badge> : <Button size="sm" variant="secondary" onClick={() => setFeePaid(member.id, f.leto, true)} data-id="element-1193" data-mp="3tpzj21hxvow7">
                  Označi kot plačano
                </Button>}
            </li>)}
        </ul>
      </Card>
    </div>;
}